// OWNER: FRONTEND-DIALER. CONTRACT: signatures fixed (LEADGEN-SPEC §11 data). Prefetch and in-memory cache for the drawer:
// GET /api/businesses/:id/drawer fires when the lead appears (alongside queue/next, never blocking the lead card), so
// pressing I renders from memory with no network call. Live checks are never persisted; dropLive() clears them.
//
// Everything here lives in page memory only: no localStorage, no IndexedDB. Live Places data (lane L) must never be
// written anywhere (Maps Platform Terms §3.2.3; LEADGEN-SPEC §8.8), so dropLive() is called when the lead's lock is
// released or the next lead loads, and a response that lands after that is thrown away.
import type { Business } from '../../../../src/shared/api';
import type { DrawerData, LiveCheck, LiveCheckRequest, ResearchResponse } from '../../../../src/shared/api-leadgen';
import { api } from '../../api';

interface Entry {
  membershipId: number | null;
  data: DrawerData | null;
  inflight: Promise<DrawerData> | null;
}

const drawers = new Map<number, Entry>();

/** Fetch (or reuse the in-flight request for) this business's drawer data. */
export function prefetchDrawer(businessId: number, membershipId: number | null): Promise<DrawerData> {
  const cur = drawers.get(businessId);
  if (cur?.inflight && cur.membershipId === membershipId) return cur.inflight;
  const entry: Entry = { membershipId, data: cur?.data ?? null, inflight: null };
  drawers.set(businessId, entry);
  const q = membershipId ? `?membershipId=${membershipId}` : '';
  const p = api.get<DrawerData>(`/api/businesses/${businessId}/drawer${q}`).then(
    (d) => {
      if (drawers.get(businessId) === entry) { entry.data = d; entry.inflight = null; }
      return d;
    },
    (e: unknown) => {
      if (drawers.get(businessId) === entry) entry.inflight = null;
      throw e;
    },
  );
  entry.inflight = p;
  return p;
}

/** Synchronous read for the I key: the cached DrawerData, or null if the prefetch hasn't landed. */
export function cachedDrawer(businessId: number): DrawerData | null {
  return drawers.get(businessId)?.data ?? null;
}

/** True while GET drawer is in flight for this business. */
export function drawerInFlight(businessId: number): boolean {
  return !!drawers.get(businessId)?.inflight;
}

/** Replace the cached DrawerData (after Verify or a research refresh), so the next I shows it without a fetch. */
export function putDrawer(businessId: number, data: DrawerData): void {
  const cur = drawers.get(businessId);
  drawers.set(businessId, { membershipId: cur?.membershipId ?? data.membershipId, data, inflight: cur?.inflight ?? null });
}

// ------------------------------------------------------------------ live checks (page memory only)

const lives = new Map<number, LiveCheck>();
/** Per business: bumped by dropLive, so responses that land after the lead left are discarded. */
const epochs = new Map<number, number>();
let globalEpoch = 0;
/** Per business: the sequence number of the newest request applied; an older response never replaces a newer one. */
const applied = new Map<number, number>();
let seq = 0;
const flights = new Map<string, Promise<LiveCheck>>();

function epochOf(businessId: number): string {
  return `${globalEpoch}.${epochs.get(businessId) ?? 0}`;
}

/** POST /api/businesses/:id/live-check. Policy (DrawerData.live.policy) decides when: 'site' on lead appear when the
 *  primary hook depends on the site; 'places' on drawer open (and on answer with 'on_open_and_connect'). */
export function runLiveCheck(businessId: number, req: LiveCheckRequest): Promise<LiveCheck> {
  const parts = [...new Set(req.parts)].sort();
  const key = `${businessId}|${parts.join(',')}|${req.hookId ?? ''}|${req.membershipId ?? ''}|${epochOf(businessId)}`;
  const same = flights.get(key);
  if (same) return same; // a second I or R while the same check is out: no second (paid) call
  const mine = ++seq;
  const epoch = epochOf(businessId);
  const body: LiveCheckRequest = { parts: parts as LiveCheckRequest['parts'] };
  if (req.hookId !== undefined) body.hookId = req.hookId;
  if (req.membershipId !== undefined) body.membershipId = req.membershipId;
  const p = api.post<LiveCheck>(`/api/businesses/${businessId}/live-check`, body).then((res) => {
    if (epochOf(businessId) === epoch && mine > (applied.get(businessId) ?? 0)) {
      lives.set(businessId, res);
      applied.set(businessId, mine);
    }
    return res;
  }).finally(() => { flights.delete(key); });
  flights.set(key, p);
  return p;
}

export function cachedLive(businessId: number): LiveCheck | null {
  return lives.get(businessId) ?? null;
}

/** Forget live data (lock released / next lead). No argument = everything. */
export function dropLive(businessId?: number): void {
  if (businessId === undefined) {
    lives.clear();
    applied.clear();
    epochs.clear();
    flights.clear();
    globalEpoch++;
    return;
  }
  lives.delete(businessId);
  applied.delete(businessId);
  epochs.set(businessId, (epochs.get(businessId) ?? 0) + 1);
  for (const k of [...flights.keys()]) if (k.startsWith(`${businessId}|`)) flights.delete(k);
}

/**
 * Verify from the drawer: the existing POST /api/businesses/:id/verify-observation (CORE, api-core.ts) with the hook's
 * sentence and evidence URL, so the Script panel's observation opener uses exactly what the setter checked.
 * Returns the updated Business, or null when the answer isn't one (the caller then patches its copy).
 */
export async function verifyObservation(businessId: number, body: { observation: string; observationSourceUrl: string }): Promise<Business | null> {
  const r = await api.post<unknown>(`/api/businesses/${businessId}/verify-observation`, body);
  return r && typeof r === 'object' && (r as Business).id === businessId && typeof (r as Business).phoneE164 === 'string' ? (r as Business) : null;
}

/** "Research this business (about $0.03)": POST /api/leadgen/research/:businessId. */
export function requestResearch(businessId: number): Promise<ResearchResponse> {
  return api.post<ResearchResponse>(`/api/leadgen/research/${businessId}`, {});
}

/** Sign-out / tests: forget everything this module holds. */
export function resetDrawerCache(): void {
  drawers.clear();
  dropLive();
}
